/* eslint-disable no-console */
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const isCustomTitleEnabledForLocale = ({
  locale,
  customTitles,
  customTitlesEnglish,
  customTitlesFrench,
}) =>
  (locale === "es" && customTitles) ||
  (locale === "en" && customTitlesEnglish) ||
  (locale === "fr" && customTitlesFrench);

const hasCustomTitlesDataForLocale = (customTitlesData = {}) =>
  (customTitlesData?.es && typeof customTitlesData?.es === "object") ||
  (customTitlesData?.en && typeof customTitlesData?.en === "object") ||
  (customTitlesData?.fr && typeof customTitlesData?.fr === "object");

const resolveCustomTitle = ({
  key,
  locale,
  t,
  customTitles,
  customTitlesEnglish,
  customTitlesFrench,
  customTitlesData = {},
}) => {
  const customTitleEnabledForLocale = isCustomTitleEnabledForLocale({
    locale,
    customTitles,
    customTitlesEnglish,
    customTitlesFrench,
  });

  const localeCustomTitlesDataExists = hasCustomTitlesDataForLocale(customTitlesData);

  // Si customTitles está habilitado para el idioma actual y customTitlesData tiene datos para ese idioma, se devuelve el valor localizado.
  if (customTitleEnabledForLocale && localeCustomTitlesDataExists) {
    const customTitleLocaleValue = customTitlesData?.[locale]?.[key];
    if (customTitleLocaleValue) {
      return customTitleLocaleValue;
    }
  }

  // Compatibilidad con antiguas versiones de customTitles. Solo si tiene antigua config de customTitles los mostramos en español.
  if (!localeCustomTitlesDataExists && customTitles && customTitlesData?.[key] && locale === "es") {
    return customTitlesData[key];
  }

  // Si no se encuentra un valor o customTitles no está habilitado para el idioma actual, se devuelve el título predeterminado.
  return t(key);
};

const useResolveCustomTitle = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n?.resolvedLanguage?.split("-")?.[0] || i18n?.language?.split("-")?.[0];
  const settingsReducer = useSelector((state) => state.settingsReducer || {});

  const {
    customTitles,
    customTitlesEnglish,
    customTitlesFrench,
    customTitlesData = {},
  } = settingsReducer;

  return useCallback(
    (key) =>
      resolveCustomTitle({
        key,
        locale,
        t,
        customTitles,
        customTitlesEnglish,
        customTitlesFrench,
        customTitlesData,
      }),
    [locale, t, customTitles, customTitlesEnglish, customTitlesFrench, customTitlesData]
  );
};

export default useResolveCustomTitle;
